import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, Select, Typography } from 'antd';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
// Constants
import { commonTimeFormat } from 'constants/global';
import { TIME_ZONE_OPTIONS } from 'constants/timeZone';
// Api
import {
  CREATE_STREAM_IMAGE_ADMIN,
  SCHEDULE_INTERVIEW_ADMIN,
} from 'api/interview/mutations';
// Types
import { InterviewStatus } from 'api/graphql-global-types';
import {
  CreateStreamImageAdmin,
  CreateStreamImageAdminVariables,
  CreateStreamImageAdmin_createStreamImageAdmin,
} from 'api/interview/types/CreateStreamImageAdmin';
import { UploadFile } from 'antd/lib/upload/interface';
import { InterviewsFormattedData } from '../../InterviewsTable/InterviewsTable';
import {
  ScheduleInterviewAdmin,
  ScheduleInterviewAdminVariables,
} from 'api/interview/types/ScheduleInterviewAdmin';
// Helpers
import {
  getTimeZoneName,
  toLegacyScheduleDateString,
  getDateWithoutCurrentTimeZone,
} from 'helpers/timeZone';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CroppedPictureInput from 'components/common/CroppedPictureInput/CroppedPictureInput';

const { Text } = Typography;
const { Option } = Select;

type ScheduleInterviewProps = {
  interview: InterviewsFormattedData | null;
  hideScheduleModal: () => void;
  isReschedule?: boolean;
};

export type UploadImage = {
  data_url?: string;
  data_key?: string;
  originFileObj?: UploadFile;
};

type ImageToUpload = {
  fields: string;
  url: string;
  name: string;
  type: string;
  file: File | '';
};

const ScheduleInterviewAdminModal = ({
  interview,
  hideScheduleModal,
  isReschedule = false,
}: ScheduleInterviewProps): JSX.Element => {
  const [, setPictureValidation] = useState<string>('');
  const [scheduleInterview, { loading }] = useMutation<
    ScheduleInterviewAdmin,
    ScheduleInterviewAdminVariables
  >(SCHEDULE_INTERVIEW_ADMIN);

  const [createStreamImageAdmin] = useMutation<
    CreateStreamImageAdmin,
    CreateStreamImageAdminVariables
  >(CREATE_STREAM_IMAGE_ADMIN);

  const getImagesToUpload = (
    preSignedUrls: CreateStreamImageAdmin_createStreamImageAdmin[],
    images: UploadImage[]
  ): ImageToUpload[] => {
    const res: ImageToUpload[] = [];

    images.forEach((img, index) => {
      const originFileObj = img?.originFileObj;
      const preSignedUrl = preSignedUrls?.[index];

      res.push({
        fields: preSignedUrl?.fields,
        url: preSignedUrl?.url,
        name: preSignedUrl?.key,
        type: originFileObj?.type || '',
        file: (originFileObj as unknown as File) || '',
      });
    });

    return res;
  };

  const uploadImages = async (
    preSignedUrls: CreateStreamImageAdmin_createStreamImageAdmin[],
    images: UploadImage[]
  ): Promise<string[]> => {
    const imagesToUpload = getImagesToUpload(preSignedUrls, images);

    for (const image of imagesToUpload) {
      const { fields, url, name, type, file } = image;
      const formData = new FormData();

      Object.entries(JSON.parse(fields)).forEach(([key, value]) => {
        formData.append(key, value as string);
      });

      formData.append('key', name);
      formData.append('Content-Type', type);
      formData.append('file', file);

      await fetch(url, {
        method: 'POST',
        body: formData,
      });
    }

    return imagesToUpload.map(({ name }) => name);
  };

  const handleScheduleInterview = async (values: any) => {
    const timeZoneOffset = momentTimezone(values.date)
      .tz(getTimeZoneName(values.tzCode))
      .utcOffset();

    const shouldUpload = Boolean(Array.isArray(values.streamImage));

    if (shouldUpload) {
      const interviewerId = interview?.interviewer?.id as string;
      const contentType = values?.streamImage[0]?.type;
      const ext = contentType?.split('/')[1];

      if (ext && contentType && interviewerId) {
        const { data } = await createStreamImageAdmin({
          variables: {
            input: {
              storeId: interviewerId,
              files: [
                {
                  contentType,
                  ext,
                },
              ],
            },
          },
        });

        try {
          if (data) {
            const imagesUrls = await uploadImages(
              data?.createStreamImageAdmin,
              values.streamImage
            );

            await scheduleInterview({
              variables: {
                input: {
                  id: interview?.id as string,
                  name: values.streamName ? values.streamName : null,
                  imageFileKey: imagesUrls[0],
                  scheduleDate: {
                    scheduleDate: toLegacyScheduleDateString(
                      moment(values.date).startOf('minute').toDate()
                    ),
                    timeZone: {
                      tzCode: values.tzCode,
                      offset: timeZoneOffset,
                    },
                  },
                },
              },
            });
            successNotification('The stream has been created successfully');
            hideScheduleModal();
          }
        } catch (error) {
          errorNotification((error as Error)?.message);
        }
      }
    } else {
      try {
        await scheduleInterview({
          variables: {
            input: {
              id: interview?.id as string,
              name: values.streamName ? values.streamName : null,
              scheduleDate: {
                scheduleDate: toLegacyScheduleDateString(
                  moment(values.date).startOf('minute').toDate()
                ),
                timeZone: {
                  tzCode: values.tzCode,
                  offset: timeZoneOffset,
                },
              },
            },
          },
        });
        successNotification('The stream has been created successfully');
        hideScheduleModal();
      } catch (error) {
        errorNotification((error as Error)?.message);
      }
    }
  };

  const [form] = Form.useForm();

  useEffect(() => {
    if (isReschedule && interview) {
      const streamDate = getDateWithoutCurrentTimeZone(
        interview?.stream?.scheduleDate
      );

      form.setFieldsValue({
        date: interview ? moment(streamDate) : null,
        tzCode: interview?.stream?.timeZone?.tzCode,
        streamName: interview.stream?.name,
        streamImage: interview.stream?.mainImageUrl,
      });
    } else {
      form.setFieldsValue({
        tzCode: 'ET',
      });
    }
  }, [interview, form, isReschedule]);

  return (
    <Form
      form={form}
      layout="horizontal"
      name="scheduleStream"
      autoComplete="off"
      onFinish={handleScheduleInterview}
    >
      <Form.Item
        name="date"
        label={<Text>Set up the date</Text>}
        rules={[
          {
            required: true,
            message: 'Please enter the date',
          },
        ]}
      >
        <DatePicker
          data-testid="streamDate"
          showTime
          format={commonTimeFormat}
          style={{ width: 250 }}
          minuteStep={15}
          showNow={false}
        />
      </Form.Item>

      <Form.Item name="tzCode" label={<Text>Time Zone</Text>}>
        <Select
          showSearch
          placeholder="Select time zone"
          optionFilterProp="children"
        >
          {TIME_ZONE_OPTIONS.map(({ tzCode, label }) => {
            return (
              <Option key={tzCode} value={tzCode}>
                {label}
              </Option>
            );
          })}
        </Select>
      </Form.Item>

      {interview?.status === InterviewStatus.Invited ||
      interview?.status === InterviewStatus.InviteViewed ? (
        <></>
      ) : (
        <>
          <Form.Item name="streamName" label={<Text>Stream Title</Text>}>
            <Input />
          </Form.Item>

          <Form.Item name="streamImage" label="Stream Cover Image">
            <CroppedPictureInput
              setPictureValidation={setPictureValidation}
              defaultImage={interview?.stream?.mainImageUrl || null}
              buttonTitle="Upload stream cover image"
            />
          </Form.Item>
        </>
      )}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ScheduleInterviewAdminModal;
